import React, { useState } from 'react';
import { Ruler } from 'lucide-react';
import { SEO } from '../components/SEO';

type LengthUnit = 
  | 'mm' | 'cm' | 'm' | 'km'  // Metric
  | 'in' | 'ft' | 'yd' | 'mi' // Imperial
  | 'nm' | 'ly';              // Special

interface ConversionState {
  value: string;
  fromUnit: LengthUnit;
  toUnit: LengthUnit;
  result: number | null;
}

function KonverterDuljine() {
  const [values, setValues] = useState<ConversionState>({
    value: '',
    fromUnit: 'm',
    toUnit: 'km',
    result: null
  });

  // Conversion factors to meters (base unit)
  const conversionFactors: Record<LengthUnit, number> = {
    // Metric
    'mm': 0.001,
    'cm': 0.01,
    'm': 1,
    'km': 1000,
    // Imperial
    'in': 0.0254,
    'ft': 0.3048,
    'yd': 0.9144,
    'mi': 1609.344,
    // Special
    'nm': 1852,          // Nautical mile
    'ly': 9.461e15      // Light year
  };

  const unitNames: Record<LengthUnit, string> = {
    // Metric
    'mm': 'Milimetar',
    'cm': 'Centimetar',
    'm': 'Metar',
    'km': 'Kilometar',
    // Imperial
    'in': 'Inč',
    'ft': 'Stopa',
    'yd': 'Jard',
    'mi': 'Milja',
    // Special
    'nm': 'Nautička milja',
    'ly': 'Svjetlosna godina'
  };

  const unitGroups = {
    metric: ['mm', 'cm', 'm', 'km'] as LengthUnit[],
    imperial: ['in', 'ft', 'yd', 'mi'] as LengthUnit[],
    special: ['nm', 'ly'] as LengthUnit[]
  };

  const calculateResult = () => {
    const inputValue = parseFloat(values.value);
    
    if (isNaN(inputValue)) {
      return null;
    }

    // Convert to base unit (meters) first, then to target unit
    const valueInMeters = inputValue * conversionFactors[values.fromUnit];
    const result = valueInMeters / conversionFactors[values.toUnit];

    return result;
  };

  const handleInputChange = (field: keyof ConversionState, value: string) => {
    setValues(prev => ({
      ...prev,
      [field]: value,
      result: null
    }));
  };

  const handleCalculate = () => {
    const result = calculateResult();
    setValues(prev => ({
      ...prev,
      result: result !== null ? parseFloat(result.toFixed(6)) : null
    }));
  };

  const formatResult = (value: number) => {
    if (Math.abs(value) < 0.000001 || Math.abs(value) > 999999999) {
      return value.toExponential(6);
    }
    return value.toString();
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Konverter Duljine",
    "applicationCategory": "CalculatorApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": "Besplatni online konverter duljine. Pretvorite metre, kilometre, stope, inče i druge mjerne jedinice.",
    "url": "https://online-kalkulator.com/konverter-duljine",
    "inLanguage": "hr",
    "author": {
      "@type": "Organization",
      "name": "Online Kalkulator"
    },
    "featureList": [
      "Konverzija metričkih jedinica",
      "Konverzija imperijalnih jedinica",
      "Posebne jedinice (nm, ly)",
      "Automatsko ažuriranje",
      "Besplatno korištenje"
    ]
  };

  return (
    <>
      <SEO 
        title="Konverter Duljine | Pretvarač Mjernih Jedinica"
        description="Besplatni online konverter duljine. Pretvorite metre, kilometre, stope, inče i druge mjerne jedinice. Jednostavan za korištenje i precizan."
        canonicalUrl="/konverter-duljine"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <Ruler className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-800 text-center">Konverter Duljine</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Converter Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="space-y-6">
              {/* Input Value */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Unesite vrijednost
                </label>
                <input
                  type="number"
                  value={values.value}
                  onChange={(e) => handleInputChange('value', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Unesite broj"
                />
              </div>

              {/* Unit Selection */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Iz jedinice
                  </label>
                  <select
                    value={values.fromUnit}
                    onChange={(e) => handleInputChange('fromUnit', e.target.value as LengthUnit)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <optgroup label="Metričke jedinice">
                      {unitGroups.metric.map(unit => (
                        <option key={unit} value={unit}>{unitNames[unit]} ({unit})</option>
                      ))}
                    </optgroup>
                    <optgroup label="Imperijalne jedinice">
                      {unitGroups.imperial.map(unit => (
                        <option key={unit} value={unit}>{unitNames[unit]} ({unit})</option>
                      ))}
                    </optgroup>
                    <optgroup label="Posebne jedinice">
                      {unitGroups.special.map(unit => (
                        <option key={unit} value={unit}>{unitNames[unit]} ({unit})</option>
                      ))}
                    </optgroup>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    U jedinicu
                  </label>
                  <select
                    value={values.toUnit}
                    onChange={(e) => handleInputChange('toUnit', e.target.value as LengthUnit)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <optgroup label="Metričke jedinice">
                      {unitGroups.metric.map(unit => (
                        <option key={unit} value={unit}>{unitNames[unit]} ({unit})</option>
                      ))}
                    </optgroup>
                    <optgroup label="Imperijalne jedinice">
                      {unitGroups.imperial.map(unit => (
                        <option key={unit} value={unit}>{unitNames[unit]} ({unit})</option>
                      ))}
                    </optgroup>
                    <optgroup label="Posebne jedinice">
                      {unitGroups.special.map(unit => (
                        <option key={unit} value={unit}>{unitNames[unit]} ({unit})</option>
                      ))}
                    </optgroup>
                  </select>
                </div>
              </div>

              {/* Convert Button */}
              <button
                onClick={handleCalculate}
                className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                Pretvori
              </button>

              {/* Result */}
              {values.result !== null && (
                <div className="border-t pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Rezultat:</span>
                    <span className="text-xl font-bold text-blue-600">
                      {formatResult(values.result)} {values.toUnit}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Information Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">O Konverteru Duljine</h2>
                <p className="text-gray-600 leading-relaxed">
                  Konverter duljine je koristan alat koji vam omogućuje jednostavno pretvaranje između
                  različitih jedinica duljine. Podržava metričke i imperijalne jedinice, kao i posebne
                  jedinice poput nautičkih milja i svjetlosnih godina.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Podržane Jedinice</h2>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Metričke jedinice:</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Milimetar (mm) = 0.001 m</li>
                      <li>Centimetar (cm) = 0.01 m</li>
                      <li>Metar (m) = osnovna jedinica</li>
                      <li>Kilometar (km) = 1000 m</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Imperijalne jedinice:</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Inč (in) = 2.54 cm</li>
                      <li>Stopa (ft) = 30.48 cm</li>
                      <li>Jard (yd) = 91.44 cm</li>
                      <li>Milja (mi) = 1.60934 km</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Posebne jedinice:</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Nautička milja (nm) = 1.852 km</li>
                      <li>Svjetlosna godina (ly) ≈ 9.461 × 10¹⁵ m</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Koristiti</h2>
                <ol className="list-decimal list-inside text-gray-600 space-y-2">
                  <li>Unesite vrijednost koju želite pretvoriti</li>
                  <li>Odaberite početnu jedinicu iz padajućeg izbornika</li>
                  <li>Odaberite željenu jedinicu za pretvorbu</li>
                  <li>Kliknite "Pretvori" za dobivanje rezultata</li>
                  <li>Rezultat će biti prikazan s odgovarajućom jedinicom</li>
                </ol>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Napomene</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Rezultati su prikazani s do 6 decimala preciznosti</li>
                  <li>Za vrlo velike ili male brojeve koristi se znanstvena notacija</li>
                  <li>Za decimalne brojeve koristite točku kao decimalni separator</li>
                  <li>Konverzije su bazirane na standardnim faktorima pretvorbe</li>
                  <li>Sve konverzije se interno računaju preko metara kao osnovne jedinice</li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default KonverterDuljine;